import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  // Pill badges per spec — fully rounded, no shadow, hairline tinted
  // backgrounds on the soft variants.
  "inline-flex items-center rounded-full border px-2.5 py-1 font-mono text-2xs font-semibold uppercase tracking-[0.08em] transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        // Solid coral — for "attention" / brand chips.
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary-hover",
        // Mint surface — neutral secondary chip (guide secondary = mint).
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "text-foreground",
        // Mint surface — positive metrics, success states, "all good".
        success: "border-success-border bg-success-muted text-success-muted-foreground",
        // Amber surface — expiring / needs-attention-soon states.
        warning: "border-warning-border bg-warning-muted text-warning-muted-foreground",
        // Blue surface — neutral informational states.
        info: "border-info-border bg-info-muted text-info-muted-foreground",
        // Partner-tier chips — tokenised so they follow a re-skin / dark mode.
        gold: "border-tier-gold-border bg-tier-gold-muted text-tier-gold-muted-foreground",
        silver: "border-tier-silver-border bg-tier-silver-muted text-tier-silver-muted-foreground",
        bronze: "border-tier-bronze-border bg-tier-bronze-muted text-tier-bronze-muted-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  /**
   * Render a leading 6×6 colored circle. Color tracks the variant's text
   * color via `currentColor`, so `<Badge variant="success" dot>` shows a
   * green dot, `<Badge variant="secondary" dot>` a mint dot, etc.
   */
  dot?: boolean;
}

export function Badge({ className, variant, dot, children, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot ? (
        <span
          aria-hidden
          className="me-1.5 inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80"
        />
      ) : null}
      {children}
    </div>
  );
}

export { badgeVariants };
